import { useMutation, useQuery } from 'react-query';
import { apiGet, apiPut } from '../services/apiClient';

/**
 * عدد الزوار — the website visitor count, and the switch that decides whether
 * it appears in the public footer.
 *
 * SPEC: DPA-FE `docs/Announcements&visitors/02-backend-api-request.md` Part B.
 *
 * ROUTES
 *
 *   GET /api/VisitorCounter/Statistics?from=&to=   the numbers      §V3
 *   GET /api/VisitorCounter/Settings               the toggle       §V4
 *   PUT /api/VisitorCounter/Settings               set the toggle   §V5
 *
 * `POST /VisitorCounter/Track` IS NOT HERE AND MUST NOT BE. It is the public
 * website's anonymous beacon — the CMS neither counts visits nor simulates
 * them, and a CMS session hitting it would pollute the very figure this screen
 * reports.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE ONE THING AN OPERATOR MUST NOT MISREAD
 * ────────────────────────────────────────────────────────────────────────────
 *
 * THE TOGGLE GOVERNS DISPLAY, NEVER COLLECTION. Turning it off removes the
 * count from the public footer; counting continues, and this screen keeps
 * showing the real figure. Requirement V5 is explicit about that, and it is
 * stated on the screen because "off" is otherwise read as "stop counting" —
 * after which the number appearing to have kept growing looks like a bug.
 *
 * ⚠ WRITTEN AHEAD OF THE BACKEND — confirmed 2026-10-05, not yet deployed.
 * Verify against the live API and record what you find in
 * `DPA-FE/docs/APICall/DPA-CONFORMANCE.md`.
 */

const BASE = 'VisitorCounter';

/**
 * Totals, windows and the daily series behind the trend chart.
 *
 * `from` / `to` are optional ISO-8601 UTC — omitted means "since launch" and
 * "now". They are sent only when set, because `apiClient.buildQuery` drops
 * empty values rather than sending `?from=`.
 *
 * NOT polled. A visitor count is not a live dashboard, and a number that ticks
 * while an operator is reading it is harder to report than one that does not.
 * The screen offers an explicit refresh instead.
 */
export const useFetchVisitorStatistics = ({ from = '', to = '' } = {}) =>
  useQuery({
    queryKey: ['visitorStatistics', from, to],
    queryFn: () => apiGet(`${BASE}/Statistics`, { from, to }),
    refetchOnWindowFocus: false,
    // `daily` must always be an array (contract §6), but a screen that draws a
    // chart from it should not be the place that discovers otherwise.
    select: (data) => ({
      ...(data ?? {}),
      daily: Array.isArray(data?.daily) ? data.daily : [],
    }),
  });

/**
 * The footer-display setting.
 *
 * `isFooterDisplayEnabled` is specified as never null — `false` when no row has
 * been saved yet, with HTTP 200 rather than a 404. The coercion below is
 * belt-and-braces: a toggle bound to `undefined` is an uncontrolled input that
 * React then warns about the moment the real value arrives.
 */
export const useFetchVisitorSettings = () =>
  useQuery({
    queryKey: ['visitorSettings'],
    queryFn: () => apiGet(`${BASE}/Settings`),
    refetchOnWindowFocus: false,
    select: (data) => ({
      isFooterDisplayEnabled: data?.isFooterDisplayEnabled === true,
      updatedOn: data?.updatedOn ?? null,
      updatedBy: data?.updatedBy ?? null,
    }),
  });

/**
 * PUT the toggle.
 *
 * The backend writes a `message` for the operator that reflects the NEW state
 * ("Visitor count is now hidden from the website footer"), but `apiClient`
 * unwraps to `data` and the envelope's `message` does not survive that — and it
 * would arrive in English into an Arabic CMS in any case, which is the problem
 * `ContactSettings`' `SERVER_MESSAGES_AR` map exists to work around. So the
 * SCREEN composes the confirmation in Arabic from the state the server
 * acknowledged. Resolve `data`, not the message, and say what came back.
 *
 * A 403 here means "known but not allowed" and carries its own `error` text;
 * `apiClient.rethrow` surfaces it and deliberately does NOT treat it as session
 * expiry, so an operator whose role does not cover this setting stays signed in.
 */
export const useUpdateVisitorSettings = () =>
  useMutation((isFooterDisplayEnabled) =>
    apiPut(`${BASE}/Settings`, { isFooterDisplayEnabled })
  );
